import React, {FC, useState} from 'react'
import {toast} from 'react-toastify'
import {KTSVG} from '../../../../_metronic/helpers'
import {Rank, Crew, Company, CompanyAdmin, Vessel} from '../core/_models'
import {CrewDeduction, getCrewLatestBankingDetails} from '../core/_requests'
import {getCrewSignOnDate} from '../core/signOnDateUtils'
import {
  blurNumberInputOnWheel,
  preventNumberInputArrowStep,
} from '../../../shared/utils/numberInputGuards'
import SearchableSelect from '../../../shared/components/SearchableSelect'

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'

const resolveCurrencyCode = (
  salaryCurrency?: string | null,
  salaryOtherCurrency?: string | null
) => {
  const normalizedAccountCurrency = (salaryCurrency || '').toString().trim().toUpperCase()
  const normalizedOtherCurrency = (salaryOtherCurrency || '').toString().trim().toUpperCase()
  if (normalizedAccountCurrency === 'OTHER') return normalizedOtherCurrency || ''
  return normalizedAccountCurrency
}

const getCurrencySymbol = (currencyCode?: string) => {
  const code = (currencyCode || '').toString().trim().toUpperCase()
  switch (code) {
    case 'USD':
      return '$'
    case 'EUR':
      return '\u20AC'
    case 'GBP':
      return '\u00A3'
    case 'SGD':
      return 'S$'
    case 'INR':
      return '\u20B9'
    case 'AED':
      return '\u062F.\u0625 '
    default:
      try {
        const part = new Intl.NumberFormat('en', {
          style: 'currency',
          currency: code,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
          .formatToParts(0)
          .find((p) => p.type === 'currency')
        const maybeSymbol = (part?.value || '').trim()
        return maybeSymbol && maybeSymbol.toUpperCase() !== code ? maybeSymbol : ''
      } catch {
        return ''
      }
  }
}

const toNumber = (value: unknown): number => {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

const getBondAmount = (claim: CrewDeduction): number => toNumber((claim as any).deductionBond)

const getOthersAmount = (claim: CrewDeduction): number => toNumber(claim.deductionOtherDeductions)

// Type for pending file uploads (for new claims)
export interface PendingAttachments {
  files: File[]
}

interface CrewDeductionModalProps {
  isOpen: boolean
  isSuperAdmin: boolean
  isManagementEntry?: boolean
  isOfficeEntry?: boolean
  showCompanyFilter?: boolean
  showSubCompanyFilter?: boolean
  canCreate: boolean
  canEditDraft?: boolean
  canReview: boolean
  isNewClaim: boolean
  lockCrewSelection?: boolean
  claim: CrewDeduction | null
  ranks: Rank[]
  crewMembers: Crew[]
  companies?: Company[]
  companyAdmins?: CompanyAdmin[]
  vessels?: Vessel[]
  selectedCompanyGroupId?: number | ''
  selectedCompanyAdminId?: number | ''
  selectedVesselId?: number | ''
  onCompanyGroupChange?: (next: number | '') => void
  onCompanyAdminChange?: (next: number | '') => void
  onVesselChange?: (next: number | '') => void
  onClose: () => void
  onSave: (pendingFiles?: File[]) => void
  onChange: (nextClaim: CrewDeduction) => void
  onAttachmentAdd: (files: FileList | null) => void
  onAttachmentRemove: (attachmentId: number) => void
  hasUsedFirstTimeDeductionForCrew?: (crewId: number) => boolean
  calculateRowTotal: (claim: CrewDeduction) => number
  formatCurrency: (value: number) => string
  isLoading: boolean
  restrictToMedicalTravelOnFirstFill?: boolean
  crewSignOnDateById?: Map<number, string>
}

const CrewDeductionModal: FC<CrewDeductionModalProps> = ({
  isOpen,
  isSuperAdmin,
  isManagementEntry = false,
  isOfficeEntry = false,
  showCompanyFilter = isSuperAdmin,
  showSubCompanyFilter = isSuperAdmin,
  canCreate,
  canEditDraft = false,
  canReview,
  isNewClaim,
  lockCrewSelection = false,
  claim,
  ranks,
  crewMembers,
  companies,
  companyAdmins,
  vessels,
  selectedCompanyGroupId,
  selectedCompanyAdminId,
  selectedVesselId,
  onCompanyGroupChange,
  onCompanyAdminChange,
  onVesselChange,
  onClose,
  onSave,
  onChange,
  onAttachmentAdd,
  onAttachmentRemove,
  hasUsedFirstTimeDeductionForCrew,
  calculateRowTotal,
  formatCurrency,
  isLoading,
  restrictToMedicalTravelOnFirstFill = false,
  crewSignOnDateById = new Map<number, string>(),
}) => {
  // State for pending file uploads (for new claims)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [selectedFileNames, setSelectedFileNames] = useState<string>('')

  const filteredCompanyAdmins = React.useMemo(() => {
    if (!companies) return []
    if (!selectedCompanyGroupId) return companies
    return companies.filter((ca) => {
      const parentCompanyId =
        (ca as any)?.cga?.id ??
        (ca as any)?.cgaid?.id ??
        (ca as any)?.companyGroupAdmin?.id ??
        (ca as any)?.companyGroupAdminId
      return parentCompanyId === Number(selectedCompanyGroupId)
    })
  }, [companies, selectedCompanyGroupId])

  React.useEffect(() => {
    if (!selectedCompanyAdminId) return
    const exists = filteredCompanyAdmins.some((ca) => ca.id === Number(selectedCompanyAdminId))
    if (!exists) {
      onCompanyAdminChange?.('')
      onVesselChange?.('')
    }
  }, [selectedCompanyAdminId, filteredCompanyAdmins, onCompanyAdminChange, onVesselChange])

  const filteredVessels = React.useMemo(() => {
    if (!vessels) return []
    const getVesselCompanyAdminId = (v: Vessel): number | undefined =>
      v.companyAdmin?.id ?? (v as any).companyAdminId ?? undefined

    const getVesselCompanyGroupId = (v: Vessel): number | undefined =>
      (v as any).companyGroupAdminId ??
      (v as any).companyGroupAdmin?.id ??
      (v.companyAdmin as any)?.cgaid?.id ??
      (v.companyAdmin as any)?.cga?.id

    if (selectedCompanyAdminId) {
      return vessels.filter((v) => getVesselCompanyAdminId(v) === Number(selectedCompanyAdminId))
    }
    if (selectedCompanyGroupId) {
      return vessels.filter((v) => getVesselCompanyGroupId(v) === Number(selectedCompanyGroupId))
    }
    return vessels
  }, [vessels, selectedCompanyAdminId, selectedCompanyGroupId])

  const withDerivedValues = (nextClaim: CrewDeduction): CrewDeduction => {
    const bondAmount = getBondAmount(nextClaim)
    const othersAmount = getOthersAmount(nextClaim)
    const deductionsTotal =
      toNumber(nextClaim.deductionCashAdvanceFromOffice) +
      bondAmount +
      othersAmount +
      toNumber(nextClaim.deductionInternetCard) +
      toNumber(nextClaim.firstTimeDeduction)

    return {
      ...nextClaim,
      deductionsTotal,
    }
  }

  if (!isOpen || !claim) return null

  const selectedCrewSignOnDate = getCrewSignOnDate(claim.crewId, crewSignOnDateById)
  const selectedCrewSignOnMonth = selectedCrewSignOnDate ? selectedCrewSignOnDate.slice(0, 7) : ''

  const updateField = (field: keyof CrewDeduction, value: string | number | any[]) => {
    const next = withDerivedValues({
      ...claim,
      [field]: value,
    })
    onChange(next)
  }

  const handleCrewChange = async (crewId: number) => {
    const selectedCrew = crewMembers.find((c) => c.id === crewId)

    const latestBanking = crewId ? await getCrewLatestBankingDetails(crewId) : null
    const hasUsedFirstTimeDeduction =
      crewId > 0 ? hasUsedFirstTimeDeductionForCrew?.(crewId) ?? false : false
    const firstTimeDeduction = hasUsedFirstTimeDeduction
      ? 0
      : toNumber(latestBanking?.deductionsTotal)
    const next = withDerivedValues({
      ...claim,
      crewId: crewId,
      crewName: selectedCrew?.name || '',
      rankId: selectedCrew?.rankId || null,
      rankName: ranks.find((r) => r.id === selectedCrew?.rankId)?.rank || '',
      vesselId: selectedCrew?.vesselId ?? selectedCrew?.vessel?.id ?? (isOfficeEntry ? claim.vesselId : null),
      vesselName: selectedCrew?.vessel?.fleet_name || selectedCrew?.vesselName || null,
      salaryCurrency: (latestBanking?.salaryCurrency ?? null) as string | null,
      salaryOtherCurrency: latestBanking?.salaryOtherCurrency ?? null,
      claimMonth: claim.claimMonth,
      firstTimeDeduction,
    })
    onChange(next)
  }

  const handleRankChange = (rankId: number) => {
    const selectedRank = ranks.find((r) => r.id === rankId)
    onChange({
      ...claim,
      rankId: rankId,
      rankName: selectedRank?.rank || '',
    })
  }

  // Handle file selection for new claims
  const handlePendingFilesChange = (files: FileList | null) => {
    if (!files) return
    const incoming = Array.from(files)
    const validFiles: File[] = []
    for (const f of incoming) {
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`File "${f.name}" exceeds 10MB limit`)
      } else {
        validFiles.push(f)
      }
    }
    if (validFiles.length > 0) {
      setPendingFiles((prev) => [...prev, ...validFiles])
      setSelectedFileNames(validFiles.map((f) => f.name).join(', '))
    }
  }

  // Remove pending file (for new claims)
  const handleRemovePendingFile = (fileName: string) => {
    setPendingFiles((prev) => prev.filter((f) => f.name !== fileName))
  }

  // Handle save with pending files
  const handleSaveClick = () => {
    if (isNewClaim) {
      onSave(pendingFiles)
      setPendingFiles([]) // Clear pending files after save
    } else {
      onSave()
    }
  }

  // Handle close and reset pending files
  const handleCloseClick = () => {
    setPendingFiles([])
    setSelectedFileNames('')
    onClose()
  }

  // Can edit only when claim is DRAFT (only for creators)
  const canEdit = (canCreate || canEditDraft) && claim.status === 'DRAFT'
  const isGeneralEditable = canEdit || isNewClaim
  const canEditAllOtherFields = isGeneralEditable
  const claimCurrency = resolveCurrencyCode(claim.salaryCurrency, claim.salaryOtherCurrency)
  const claimCurrencySymbol = getCurrencySymbol(claimCurrency)
  const deductionsTotal = toNumber(claim.deductionsTotal)
  const firstTimeDeduction = toNumber(claim.firstTimeDeduction)
  const bondAmount = getBondAmount(claim)
  const othersAmount = getOthersAmount(claim)

  return (
    <div className='modal-overlay' onClick={handleCloseClick}>
      <div
        className='modal-content'
        onClick={(e) => e.stopPropagation()}
        style={{maxWidth: '1100px', maxHeight: '850px'}}
      >
        <div className='custom-modal-header d-flex justify-content-between align-items-center'>
          <h5 className='m-0'>{isNewClaim ? 'Add New Deduction' : 'Crew Deduction Details'}</h5>
          <button className='close-btn' onClick={handleCloseClick}>
            <KTSVG path='/media/map/x.svg' className='svg-icon-2x' />
          </button>
        </div>

        <div className='custom-modal-body' style={{maxHeight: '65vh', overflowY: 'auto'}}>
          <div className='row g-4'>
            {isNewClaim && (isSuperAdmin || isManagementEntry) && (
              <>
                {showCompanyFilter && <div className='col-md-4'>
                  <label className={LABEL}>Company</label>
                  <SearchableSelect
                    className='form-select'
                    value={selectedCompanyGroupId ?? ''}
                    onChange={(event) =>
                      onCompanyGroupChange?.(event.target.value ? Number(event.target.value) : '')
                    }
                  >
                    <option value=''>All Companies</option>
                    {(companyAdmins || []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>}

                {showSubCompanyFilter && (!selectedCompanyGroupId || filteredCompanyAdmins.length > 0) && (
                  <div className='col-md-4'>
                    <label className={LABEL}>Sub-Company</label>
                    <SearchableSelect
                      className='form-select'
                      value={selectedCompanyAdminId ?? ''}
                      onChange={(event) =>
                        onCompanyAdminChange?.(event.target.value ? Number(event.target.value) : '')
                      }
                    >
                      <option value=''>All Sub-Companies</option>
                      {filteredCompanyAdmins.map((ca) => (
                        <option key={ca.id} value={ca.id}>
                          {ca.name}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                )}

                {!isOfficeEntry && <div className='col-md-4'>
                  <label className={LABEL}>Vessel</label>
                  <SearchableSelect
                    className='form-select'
                    value={selectedVesselId ?? ''}
                    onChange={(event) =>
                      onVesselChange?.(event.target.value ? Number(event.target.value) : '')
                    }
                  >
                    <option value=''>All Vessels</option>
                    {filteredVessels.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.fleet_name}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>}
              </>
            )}

            {isOfficeEntry && (
              <div className='col-md-4'>
                <label className={LABEL} htmlFor='deduction-entry-vessel'>
                  Vessel <span className='text-danger'>*</span>
                </label>
                <SearchableSelect
                  id='deduction-entry-vessel'
                  required
                  className='form-select'
                  value={claim.vesselId || ''}
                  disabled={!isGeneralEditable || isLoading}
                  onChange={(event) => {
                    const vesselId = event.target.value ? Number(event.target.value) : null
                    const vessel = (vessels || []).find((v) => v.id === vesselId)
                    onChange({...claim, vesselId, vesselName: vessel?.fleet_name || null})
                  }}
                >
                  <option value=''>Select Vessel</option>
                  {filteredVessels.map((vessel) => (
                    <option key={vessel.id} value={vessel.id}>{vessel.fleet_name}</option>
                  ))}
                </SearchableSelect>
              </div>
            )}

            {/* Crew Member - Dropdown for new claims */}
            <div className='col-md-4'>
              <label className={LABEL}>
                Crew Member <span className='text-danger'>*</span>
              </label>
              {isNewClaim && !lockCrewSelection ? (
                <SearchableSelect
                  className='form-select'
                  value={claim.crewId || ''}
                  onChange={(event) => handleCrewChange(Number(event.target.value))}
                >
                  <option value=''>Select Crew Member</option>
                  {crewMembers.map((crew) => (
                    <option key={crew.id} value={crew.id}>
                      {crew.name}
                    </option>
                  ))}
                </SearchableSelect>
              ) : (
                <input type='text' className='form-control' value={claim.crewName || ''} disabled />
              )}
            </div>

            {/* Rank - Dropdown */}
            <div className='col-md-4'>
              <label className={LABEL}>
                Rank <span className='text-danger'>*</span>
              </label>
              <input
                type='text'
                className='form-control'
                value={claim.rankName || ''}
                placeholder='Select Crew Member'
                disabled
              />
            </div>

            {/* Status - Only show for existing claims, not for new claims */}
            {!isNewClaim && (
              <div className='col-md-4'>
                <label className={LABEL}>Status</label>
                <input type='text' className='form-control' value={claim.status} disabled />
              </div>
            )}

            {/* Claim Month */}
            <div className={isNewClaim ? 'col-md-4' : 'col-md-4'}>
              <label className={LABEL}>Claim Month</label>
              <input
                type='month'
                className='form-control'
                value={claim.claimMonth ? claim.claimMonth.slice(0, 7) : ''}
                min={selectedCrewSignOnMonth || undefined}
                disabled
              />
              {selectedCrewSignOnMonth && (
                <small className='text-muted'>
                  This month follows the page month filter and cannot be before sign-on month (
                  {selectedCrewSignOnMonth}).
                </small>
              )}
            </div>

            <div className='col-12'>
              <div className='p-0'>
                <div className='fw-semibold text-dark mb-2'>Deductions</div>
                <div className='row g-3'>
                  <div className='col-md-3'>
                    <label className={LABEL}>Cash Advance</label>
                    <input
                      type='number'
                      className='form-control'
                      placeholder='0.00'
                      value={claim.deductionCashAdvanceFromOffice ? claim.deductionCashAdvanceFromOffice : ''}
                      onWheel={blurNumberInputOnWheel}
                      onKeyDown={preventNumberInputArrowStep}
                      onChange={(event) =>
                        updateField(
                          'deductionCashAdvanceFromOffice',
                          event.target.value === '' ? 0 : Number(event.target.value)
                        )
                      }
                      min={0}
                      disabled={!canEditAllOtherFields}
                    />
                  </div>
                  <div className='col-md-3'>
                    <label className={LABEL}>Bond</label>
                    <input
                      type='number'
                      className='form-control'
                      placeholder='0.00'
                      value={bondAmount ? bondAmount : ''}
                      onWheel={blurNumberInputOnWheel}
                      onKeyDown={preventNumberInputArrowStep}
                      onChange={(event) =>
                        updateField(
                          'deductionBond' as keyof CrewDeduction,
                          event.target.value === '' ? 0 : Number(event.target.value)
                        )
                      }
                      min={0}
                      disabled={!canEditAllOtherFields}
                    />
                  </div>
                  <div className='col-md-3'>
                    <label className={LABEL}>Deduction - Internet Card</label>
                    <input
                      type='number'
                      className='form-control'
                      placeholder='0.00'
                      value={claim.deductionInternetCard ? claim.deductionInternetCard : ''}
                      onWheel={blurNumberInputOnWheel}
                      onKeyDown={preventNumberInputArrowStep}
                      onChange={(event) =>
                        updateField(
                          'deductionInternetCard',
                          event.target.value === '' ? 0 : Number(event.target.value)
                        )
                      }
                      min={0}
                      disabled={!canEditAllOtherFields}
                    />
                  </div>
                  <div className='col-md-3'>
                    <label className={LABEL}>Others</label>
                    <input
                      type='number'
                      className='form-control'
                      placeholder='0.00'
                      value={othersAmount ? othersAmount : ''}
                      onWheel={blurNumberInputOnWheel}
                      onKeyDown={preventNumberInputArrowStep}
                      onChange={(event) =>
                        updateField(
                          'deductionOtherDeductions',
                          event.target.value === '' ? 0 : Number(event.target.value)
                        )
                      }
                      min={0}
                      disabled={!canEditAllOtherFields}
                    />
                  </div>
                  {firstTimeDeduction > 0 && (
                    <div className='col-md-3'>
                      <label className={LABEL}>1st time deduction</label>
                      <input
                        type='text'
                        className='form-control'
                        value={`${claimCurrencySymbol}${formatCurrency(firstTimeDeduction)}`}
                        readOnly
                      />
                    </div>
                  )}
                  <div className='col-md-3'>
                    <label className={LABEL}>Total Deduction</label>
                    <input
                      type='text'
                      className='form-control fw-bold'
                      value={`${claimCurrencySymbol}${formatCurrency(deductionsTotal)}`}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Remarks */}
            <div className='col-md-6'>
              <label className={LABEL}>Remarks</label>
              <textarea
                className='form-control'
                rows={3}
                placeholder='Enter any remarks or notes...'
                value={claim.remarks || ''}
                onChange={(event) => updateField('remarks', event.target.value)}
                disabled={!canEditAllOtherFields}
              />
            </div>

            {/* Attachments */}
            <div className='col-md-6'>
              <label className={LABEL}>Attachments</label>

              {/* File input - show for new claims or editable existing claims */}
              {(isNewClaim || canEdit) && (
                <div className='input-group mb-2'>
                  <input
                    type='text'
                    className='form-control'
                    placeholder='No file chosen'
                    value={
                      isNewClaim
                        ? pendingFiles.length > 0
                          ? pendingFiles.map((f) => f.name).join(', ')
                          : ''
                        : selectedFileNames || ''
                    }
                    readOnly
                  />
                  <button
                    type='button'
                    className='btn btn-light'
                    onClick={() => document.getElementById('crew-claim-file-input')?.click()}
                  >
                    Choose Files
                  </button>
                  <input
                    id='crew-claim-file-input'
                    type='file'
                    className='d-none'
                    multiple
                    onChange={(event) => {
                      const files = event.target.files
                      if (isNewClaim) {
                        handlePendingFilesChange(files)
                      } else {
                        if (files && files.length > 0) {
                          const validFiles: File[] = []
                          for (const f of Array.from(files)) {
                            if (f.size > 10 * 1024 * 1024) {
                              toast.error(`File "${f.name}" exceeds 10MB limit`)
                            } else {
                              validFiles.push(f)
                            }
                          }
                          if (validFiles.length > 0) {
                            setSelectedFileNames(
                              validFiles
                                .map((f) => f.name)
                                .join(', ')
                            )
                            onAttachmentAdd(files)
                          }
                        }
                      }
                      // Reset input value to allow selecting same file again
                      event.target.value = ''
                    }}
                  />
                </div>
              )}

              {/* Pending files for new claims */}
              {isNewClaim && pendingFiles.length > 0 && (
                <div className='d-flex flex-wrap gap-2 mb-2'>
                  {pendingFiles.map((file, idx) => (
                    <span
                      key={`${file.name}-${idx}`}
                      className='badge text-dark d-flex align-items-center'
                      style={{background: '#f1f1f2', fontWeight: 500}}
                      title={file.name}
                    >
                      {file.name}
                      <span
                        role='button'
                        tabIndex={0}
                        aria-label={`Remove ${file.name}`}
                        className='ms-2'
                        style={{cursor: 'pointer', color: '#d9214e', fontWeight: 700}}
                        onClick={() => handleRemovePendingFile(file.name)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleRemovePendingFile(file.name)
                          }
                        }}
                      >
                        ×
                      </span>
                    </span>
                  ))}
                </div>
              )}

              {isNewClaim && pendingFiles.length === 0 && (
                <small className='text-muted d-block mb-2'>
                  Select files to attach to this deduction.
                </small>
              )}

              {/* Existing attachments for saved claims */}
              {!isNewClaim && (
                <div
                  className='d-flex flex-column gap-1'
                  style={{maxHeight: 120, overflowY: 'auto'}}
                >
                  {!claim.attachments || claim.attachments.length === 0 ? (
                    <span className='text-muted fs-7'>No attachments</span>
                  ) : (
                    claim.attachments.map((file) => (
                      <div
                        key={file.id}
                        className='d-flex align-items-center justify-content-between border rounded px-2 py-1'
                        style={{backgroundColor: '#f8f9fa'}}
                      >
                        <span
                          className='text-truncate fs-7'
                          style={{maxWidth: 150}}
                          title={file.fileName}
                        >
                          {file.fileName}
                        </span>
                        {canEdit && (
                          <button
                            className='btn btn-sm btn-outline-danger'
                            onClick={() => onAttachmentRemove(file.id)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className='custom-modal-footer d-flex justify-content-end gap-2 border-top px-4 py-3'>
          <button type='button' className='btn btn_secondary' onClick={handleCloseClick}>
            Cancel
          </button>
          {(canEdit || isNewClaim) && (
            <button
              type='button'
              className='btn btn_primary'
              onClick={handleSaveClick}
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : isNewClaim ? 'Create Deduction' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default CrewDeductionModal
